import { View, ViewProps } from 'react-native';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '@/utils/cn';

const stackVariants = cva('flex', {
  variants: {
    direction: {
      vertical: 'flex-col',
      horizontal: 'flex-row',
    },
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      5: 'gap-5',
      6: 'gap-6',
      8: 'gap-8',
      10: 'gap-10',
      12: 'gap-12',
      16: 'gap-16',
      20: 'gap-20',
      24: 'gap-24',
    },
    align: {
      start: 'items-start',
      end: 'items-end',
      center: 'items-center',
      baseline: 'items-baseline',
      stretch: 'items-stretch',
    },
    justify: {
      start: 'justify-start',
      end: 'justify-end',
      center: 'justify-center',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    wrap: {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      'wrap-reverse': 'flex-wrap-reverse',
    },
    flex: {
      1: 'flex-1',
      none: 'flex-none',
      auto: 'flex-auto',
    },
  },
  defaultVariants: {
    direction: 'vertical',
    gap: 4,
    align: 'stretch',
    justify: 'start',
    wrap: 'nowrap',
  },
});

export interface StackProps
  extends ViewProps, VariantProps<typeof stackVariants> {
  className?: string;
  children: React.ReactNode;
}

const Stack = forwardRef<View, StackProps>(
  (
    { className, direction, gap, align, justify, wrap, flex, ...props },
    ref
  ) => {
    return (
      <View
        ref={ref}
        className={cn(
          stackVariants({ direction, gap, align, justify, wrap, flex }),
          className
        )}
        {...props}
      />
    );
  }
);

Stack.displayName = 'Stack';

// Convenience components
const VStack = forwardRef<View, Omit<StackProps, 'direction'>>((props, ref) => (
  <Stack ref={ref} direction="vertical" {...props} />
));
VStack.displayName = 'VStack';

const HStack = forwardRef<View, Omit<StackProps, 'direction'>>((props, ref) => (
  <Stack ref={ref} direction="horizontal" {...props} />
));
HStack.displayName = 'HStack';

export { Stack, VStack, HStack, stackVariants };
